import { useState, useEffect } from "react";
import DataTable from "@/components/organisms/DataTable";
import { ColumnDef } from "@tanstack/react-table";
import ColumnHeader from "@/components/atoms/ColumnHeader";
import PageHeader from "@/components/molecules/PageHeader";
import Button from "@/components/atoms/Button";
import { ArrowUpDown, Plus } from "lucide-react";
import Link from "@/components/atoms/Link";
import Badge from "@/components/atoms/Badge";
import SearchBox from "@/components/molecules/SearchBox";
import Container from "@/components/atoms/Container";
import useAccess from "@/hooks/useAccess";
import Sow from "@/types/Sow";
import { Account } from "@/types/Account";
import SowFilter from "@/components/organisms/Sow/Filter";
import axios from "axios";
import Pagination from "@/components/atoms/Pagination";
import { X } from "lucide-react";
import useSowTableColumnStore from "@/store/sowTableColumn";
import Select from "@/components/molecules/Select";
import { router } from "@inertiajs/react";
import TableSettings from "@/components/molecules/TableSettings";

type Props = {
  accounts: Account[];
  payment_types: string[];
  payment_systems: string[];
  resources: any[];
};

const selectOptions = [
  { id: "id", label: "ID" },
  { id: "name", label: "Name" },
  { id: "paymentType", label: "Payment Type" },
  { id: "startDate", label: "Start Date" },
  { id: "endDate", label: "End Date" },
  { id: "createdBy", label: "Owner" },
  { id: "adminApprove", label: "Admin Approval" },
  { id: "details", label: "Details" },
  { id: "shortDescription", label: "Short Description" },
];

const Index = ({ accounts, payment_types, payment_systems, resources }: Props) => {
  const [sows, setSows] = useState<Sow[]>([]);
  const [filters, setFilters] = useState<{ [key: string]: any }>({});
  const [pagination, setPagination] = useState({ page: 1, per_page: 10 });
  const [totalPages, setTotalPages] = useState<number>(0);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [search, setSearch] = useState<string>("");
  const [showResetFilter, setshowResetFilter] = useState<boolean>(false);
  const [exportMode, setExportMode] = useState<boolean>(false);
  const [exportButtonText, setExportButtonText] = useState<string>("Export Table");
  const [isPaginationUpdatedInternally, setIsPaginationUpdatedInternally] = useState(false);
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);
  const {selectedColumns, setSelectedColumns } = useSowTableColumnStore();
  const [sorting, setSorting] = useState<{ column: string; direction: string | undefined }>({ column: "", direction: undefined });

  const allColumns: ColumnDef<Sow>[] = [
    {
      id: "id",
      accessorKey: "display_id",
      header: ({ column }) => <ColumnHeader column={column}>ID</ColumnHeader>,
    },
    {
      id: "name",
      accessorKey: "name",
      header: ({ column }) => <ColumnHeader column={column}>Name</ColumnHeader>,
    },
    {
      id: "paymentType",
      accessorKey: "payment_type",
      header: ({ column }) => <ColumnHeader column={column}>Payment Type</ColumnHeader>,
    },
    {
      id: "startDate",
      accessorKey: "start_date",
      header: ({ column }) => <ColumnHeader column={column} onSortChange={handleSortChange}>Start Date</ColumnHeader>,
    },
    {
      id: "endDate",
      accessorKey: "end_date",
      header: ({ column }) => <ColumnHeader column={column} onSortChange={handleSortChange}>End Date</ColumnHeader>,
    },
    {
      id: "createdBy",
      accessorKey: "created_by.name",
      header: ({ column }) => <ColumnHeader column={column} onSortChange={handleSortChange}>Owner</ColumnHeader>,
      cell: ({ row }) => (
        <div className="flex items-center space-x-2">
          <img
            src={row.original.created_by.avatar_url || "/default_avatar.png"}
            alt={row.original.created_by.name}
            className="w-8 h-8 rounded-full dark:bg-darkTextColor"
          />
          <span>{row.original.created_by.name}</span>
        </div>
      ),
    },
    {
      id: "adminApprove",
      accessorKey: "admin_approve",
      header: ({ column }) => <ColumnHeader column={column}>Admin Approval</ColumnHeader>,
      cell: ({ row }) => {
        const status = row.original.admin_approval;
        const badgeStatus =
          status ? "success" : "destructive";
  
        return (
          <div className="space-x-2">
            <Badge variant={badgeStatus} className="p-1" />
            <span>{status ? "APPROVED" : "Not APPROVED"}</span>
          </div>
        );
      },
    },
    {
      id: "details",
      accessorKey: "details",
      header: ({ column }) => <ColumnHeader column={column}>Details</ColumnHeader>,
    },
    {
      id: "shortDescription",
      accessorKey: "short_description",
      header: ({ column }) => <ColumnHeader column={column}>Short Description</ColumnHeader>,
    },
  ];
  
  const handleSortChange = (column: string, direction: string | undefined) => {
    setSorting({ column, direction });
  };

  const columns = allColumns.filter(column => column.id && selectedColumns.includes(column.id));

  useEffect(() => {
    const fetchSows = async () => {
      const queryParams: Record<string, any> = {
        ...filters,
        ...pagination,
        column: sorting.column,
        direction: sorting.direction,
      };

      if (search) {
        queryParams.search = search;
      }

      const response = await axios.get('/sows/listing', {
        params: queryParams
      });

      setSows(response.data.sows);
      setTotalCount(response.data.total_count);
      setTotalPages(response.data.total_pages);

      if (response.data.current_page !== pagination.page) {
        setPagination(prev => ({ ...prev, page: response.data.current_page }));
        setIsPaginationUpdatedInternally(true);
      } else {
        setIsPaginationUpdatedInternally(false);
      }
      
    };

    if (!isPaginationUpdatedInternally) {
      fetchSows();
    }

    return () => {
      setIsPaginationUpdatedInternally(false);
    };
  }, [filters, pagination, search, sorting]);

  const { getAccess } = useAccess();
  
  const breadcrumbItems = [
    {
      label: "Sows",
      onClick: () => router.get("/sows"),
    },
  ];

  const handleFilterChange = (filterId: string, value: any) => {
    if (value !== null) {
      setshowResetFilter(true);
    }

    if (filterId === 'account_id') {
      setSelectedAccounts(value as string[]);
    }

    setFilters(prev => ({ ...prev, [filterId]: value }));
  };

  const removeTag = (value: string) => {
    setSelectedAccounts(selectedAccounts.filter((item) => item !== value));
  };

  const removeColumnTag = (value: string) => {
    setSelectedColumns(selectedColumns.filter((item) => item !== value));
  };

  const handlePageChange = (page: number) => {  
    setPagination(prev => ({ ...prev, page }));
  };

  const handleSearch = (search: string) => {
    setshowResetFilter(true);
    setSearch(search);
    setPagination({ page: 1, per_page: 10 });
  };

  const resetFilters = () => {
    handleFilterChange('account_id', null);
    handleFilterChange('payment_type', null);
    handleFilterChange('payment_system', null);
    handleFilterChange('resource_emails', null);
    handleFilterChange('admin_approval', null);
    setshowResetFilter(false);
    setPagination({ page: 1, per_page: 10 });
    setSearch("");
  };

  const handleExport = () => {
    const exportUsers = async () => {
      setExportMode(true);
      const queryParams: Record<string, any> = {
        ...filters
      };

      await axios.get('/sows/export', {
        params: queryParams
      }).then(response => {
        if (response.status === 200 && response.data && response.data.jid) {
          const jobId = response.data.jid;

          setExportButtonText('Exporting...');
          checkExportStatus(jobId);
        }
      })
      .catch(error => {
        console.error('Error exporting data:', error);
      });
    };

    exportUsers();
  }

  const checkExportStatus = async (jobId: string) => {
    try {
      const response = await axios.get(`/sows/export_status/${jobId}`);
      console.log(response.data.status)
      if (response.data.status === 'complete') {
        setExportMode(false);
        setExportButtonText('Export Table');
        window.location.href = `/sows/export_download/${jobId}`;
      } else if(response.data.status === 'failed') {
        setExportMode(false);
        setExportButtonText('Export Table');
        alert('Export failed. Please contact administrator.');
      } else {
        if (response.data.percentage) {
          setExportButtonText(`Exporting... ${response.data.percentage}%`);
        }

        setTimeout(() => checkExportStatus(jobId), 1000);
      }
    } catch (error) {
      console.error('Error checking export status:', error);
    }
  };
  return (
    <>
      <Container>
        <PageHeader title="SOW Management" breadcrumbItems={breadcrumbItems}>
          <div className="space-x-2">
          {getAccess(["create_sow"]) ? (
            <Link href="/sows/new">
              <Button className="inline-flex items-center gap-2 text-black">
                <Plus size={16} /> <span>Create Sow</span>
              </Button>
            </Link>
          ) : (
            <span className="inline-flex items-center gap-2 text-gray-500 cursor-not-allowed">
              <Button className="inline-flex items-center gap-2 text-black" disabled>
                <Plus size={16} /> <span>Create Sow</span>
              </Button>
            </span>
          )}
          </div>
        </PageHeader>
        <div className="border-b border-border" />
        <div className="flex items-center justify-between pt-6 pb-4">
          <SearchBox onSearch={handleSearch} value={search} placeholder="Search by ID"/>
          <div className="flex gap-2 ">
            <TableSettings selectOptions={selectOptions} selectedColumns={selectedColumns} setSelectedColumns={setSelectedColumns} />
            <div className="flex gap-1">
              <Button
                variant="outline"
                className="inline-flex items-center gap-2 border-border"
                onClick={handleExport}
                disabled={!getAccess(["export_sows"]) || exportMode}
              >
                <ArrowUpDown size={16} /> <span>{exportButtonText}</span>
              </Button>
            </div>
          </div>
          
        </div>
        <div className="flex flex-wrap gap-2 mt-2">
          {(selectedAccounts || []).map((item) => (
            <span key={item} className="bg-gray-200 dark:bg-primary dark:text-black px-2 py-1 rounded flex items-center gap-1 text-xs">
              {accounts.find((opt) => opt.id.toString() == item)?.name}
              <button onClick={() => removeTag(item)}>
                <X size={16} />
              </button>
            </span>
          ))}
        </div>
        <div className="flex items-center justify-between pt-6 pb-4">
          <SowFilter 
            filters={[
              {
                id: "account_id",
                label: "By Account",
                options: accounts.map(account => ({ value: account.id, label: account.name})),
                selected: selectedAccounts
              },
              {
                id: "payment_type",
                label: "By Payment Type",
                options: payment_types.map(payment_type => ({ value: payment_type, label: payment_type})),
              },
              {
                id: "resource_emails",
                label: "By Resource",
                options: resources.map(resource => ({ value: resource[2], label: `${resource[0]} ${resource[1]}`})),
              },
              {
                id: "admin_approval",
                label: "By Approval",
                options: [{ value: true, label: `APPROVED`}, { value: false, label: `NOT APPROVED`}],
              }
            ]}
            selectedFilters={filters}
            onFilterChange={handleFilterChange}
          />
          {showResetFilter && (
            <div className="flex flex-col">
              <Button onClick={resetFilters}>Reset Filters</Button>
            </div>
          )}
        </div>

        <DataTable
          columns={columns}
          data={sows}
          rowUrl={(row) => `${location.pathname}/${row.id}`}
          useBackendSorting={true}
        />
        
        <Pagination
          currentPage={pagination.page}
          totalPages={totalPages}
          totalCount={totalCount}
          perPage={pagination.per_page}
          onPageChange={handlePageChange}
        />
      </Container>
      
      
      
    </>
  );
};

export default Index;
